// THE VALUE STEP'S TWO SHEETS (WEB-PARITY-4): the suggested-value sheet (`SuggestedValueSheet`,
// SuggestedValue.swift:2227-2577; VD-10, VD-16) and the "Quick check" (`LargeGapVerificationView`; VD-12). Every figure is
// the server's; the only arithmetic here is the sheet's preview of what the owner is still typing (parity4Rules.ts says
// why that one runs on the page).

import { useState } from "react";
import type { AskSource, EvaluateCaseResponse, ValueResponse } from "@contract";
import { money } from "../../format";
import { Icon, Marked, Said4, Sheet4 } from "./parity4Parts";
import {
  confidenceWord, dollars, lgExplanation, lgReasoning, lgSizeLine, percentString, precisionLabel, ratioTerm, sheetBelowLine,
  sizeBullet, typedAssessedNote, typedNotes,
} from "./parity4Rules";
import { say4, say4With } from "./parity4Wording";

type Uniformity = EvaluateCaseResponse["body"]["uniformity"];

/** The owner's typing, as a figure: digits and one point, or nothing. */
function parsed(text: string): number | null {
  const digits = text.replace(/[^0-9.]/g, "");
  if (digits === "") return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
}

export function SuggestedValueSheet({
  value, uniformity, state, assessed, onCancel, onUse,
}: {
  value: ValueResponse;
  uniformity: Uniformity;
  state: string | null;
  assessed: number | null;
  onCancel: () => void;
  onUse: (amount: number, source: AskSource) => void;
}) {
  const v = value.body.value;
  const s = v.suggestion!;
  const ratio = v.ratio;
  const current = value.body.ask.requestedValue;
  const [text, setText] = useState(String(current?.amount ?? s.value.amount));
  const typed = parsed(text);
  const shown = typed ?? current?.amount ?? s.value.amount;
  const basis = v.comparisonBasis.amount?.amount ?? null;
  const fractional = ratio.fractional;
  const pct = percentString(ratio.value);
  const term = ratioTerm(state);
  const word = confidenceWord(s.confidence);
  // The adopted equity ground's market figure, where it sits BELOW the comp value (:2196-2206, :2466-2468).
  const floor = uniformity !== null && uniformity.adopted && uniformity.equityMarket !== null
    && uniformity.equityMarket.amount < s.value.amount ? uniformity : null;
  const below = sheetBelowLine(basis, shown, fractional);
  const headlineNote = typedAssessedNote(shown, ratio, state);
  const fieldNote = typedAssessedNote(typed, ratio, state);
  const notes = typedNotes(typed, s, assessed, fractional);
  const source: AskSource = typed === null || typed === s.value.amount ? "engine" : "owner";

  return (
    <Sheet4
      titleId="sheet.title"
      cancelId="sheet.cancel"
      onCancel={onCancel}
      kind="suggested-value"
      footer={
        <button
          type="button"
          className="btn primary block"
          onClick={() => onUse(Math.max(0, shown), source)}
          data-held-copy="sheet.use"
        >
          {say4With("sheet.use", { amount: dollars(shown) })}
        </button>
      }
    >
      <div className="sheet-headline">
        <Said4 id={fractional ? "sheet.eyebrowMarket" : "sheet.eyebrow"} as="p" className="eyebrow muted-eyebrow" />
        <p className="sheet-amount">{dollars(shown)}</p>
        {headlineNote !== null && <p className="faint">{headlineNote}</p>}
        {below !== null && <p className="muted" data-held-copy="sheet.below">{below}</p>}
      </div>

      <section className="sheet-card" data-supported-range>
        <h3><Icon name="target" size={14} /> <Said4 id="sheet.range.title" /></h3>
        {s.high.amount > s.low.amount && (
          <p className="sheet-range" data-held-copy="sheet.range">{say4With("sheet.range", { low: money(s.low), high: money(s.high) })}</p>
        )}
        {s.tempered ? (
          <Said4 id="sheet.tempered" as="p" className="muted small" values={{ indicated: money(s.indicatedValue), word, value: money(s.value) }} />
        ) : s.high.amount > s.low.amount ? (
          <Said4 id="sheet.untempered" as="p" className="muted small" values={{ value: money(s.value) }} />
        ) : null}
        <p className={`sheet-precision precision-${s.confidence}`}>
          <Icon name={s.confidence === "low" ? "warn" : "seal"} size={14} /> {precisionLabel(s.confidence)}
        </p>
        <p className="muted small" data-copy-code={s.confidenceBlurb.code}>{s.confidenceBlurb.text}</p>
        <Said4 id="sheet.precisionNote" as="p" className="faint" />
        {s.weakEvidence && (
          <p className="faint" data-held-copy="sheet.weak">
            {say4With("sheet.weak", {
              comps: s.compCount === 1 ? say4("sheet.weak.one") : say4With("sheet.weak.many", { count: s.compCount }),
            })}
          </p>
        )}
        {s.reachOnly && <Said4 id="sheet.reach" as="p" className="faint" />}
      </section>

      <section className="sheet-card" data-how-we-got-this>
        <h3><Icon name="info" size={14} /> <Said4 id="sheet.how.title" /></h3>
        {s.ratePerSquareFoot !== null && s.compCount > 0 ? (
          <>
            <ul className="sheet-bullets">
              {fractional ? (
                <>
                  <li><Said4 id="sheet.how.fractional" values={{ pct, basis: basis === null ? "—" : dollars(basis) }} /></li>
                  <li>
                    <Said4 id="sheet.how.derivedBelowThat" values={{
                      count: s.compCount, sales: say4(s.compCount === 1 ? "sheet.how.saleThat" : "sheet.how.salesThat"),
                    }} />
                  </li>
                </>
              ) : (
                <li>
                  <Said4 id="sheet.how.derivedBelow" values={{
                    count: s.compCount, sales: say4(s.compCount === 1 ? "sheet.how.saleThat" : "sheet.how.salesThat"),
                    basis: basis === null ? "—" : dollars(basis),
                  }} />
                </li>
              )}
              <li><Said4 id="sheet.how.sizeRate" values={{ rate: dollars(s.ratePerSquareFoot) }} /></li>
              <li><Marked text={sizeBullet(s, fractional, pct, term)} /></li>
              {s.tempered && (
                <li><Said4 id="sheet.how.tempered" values={{ word, value: money(s.value), indicated: money(s.indicatedValue) }} /></li>
              )}
              {s.flooredAboveIndicators && <li><Said4 id="sheet.how.floored" /></li>}
              {floor !== null && floor.equityMarket !== null && floor.equityValue !== null && (
                <li>
                  <Said4 id="sheet.how.equity" values={{
                    value: money(s.value), floor: money(floor.equityMarket), floorAssessed: money(floor.equityValue),
                  }} />
                </li>
              )}
            </ul>
            <Said4 id={s.tempered ? "sheet.how.moreComps" : "sheet.how.documented"} as="p" className="muted small" />
          </>
        ) : (
          <Said4 id="sheet.how.thin" as="p" />
        )}
        <Said4 id="sheet.how.target" as="p" className="faint" />
      </section>

      <section className="sheet-card" data-own-amount>
        <h3><Icon name="pencil" size={14} /> <Said4 id="sheet.own.title" /></h3>
        <label className="manual-field">
          <Said4 id={fractional ? "sheet.own.labelMarket" : "sheet.own.label"} as="span" />
          <span className="dollar-input">
            <span aria-hidden="true">$</span>
            <input type="text" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} />
          </span>
        </label>
        {fieldNote !== null && <p className="faint">{fieldNote}</p>}
        {notes.map((note) => <p key={note} className="sheet-note">{note}</p>)}
      </section>
    </Sheet4>
  );
}

// ── the "Quick check" (LargeGapVerificationView.swift) ────────────────────────────────────────────────────────────

export function QuickCheck({
  gap, onCancel, onAcknowledge, onFixRecord,
}: {
  gap: NonNullable<ValueResponse["body"]["value"]["largeGap"]>;
  onCancel: () => void;
  onAcknowledge: (reasons: string[]) => void;
  onFixRecord: () => void;
}) {
  const [looksRight, setLooksRight] = useState<boolean | null>(null);
  const size = lgSizeLine(gap);
  return (
    <Sheet4
      titleId="lg.title"
      cancelId="lg.cancel"
      onCancel={onCancel}
      kind="quick-check"
      footer={
        <>
          {looksRight === false && <Said4 id="lg.fixFirst" as="p" className="muted small" />}
          {looksRight !== false && gap.sizeMismatch && <Said4 id="lg.canContinue" as="p" className="muted small" />}
          <button
            type="button"
            className="btn primary block"
            disabled={looksRight === null}
            onClick={() => (looksRight === false ? onFixRecord() : onAcknowledge(lgReasoning(gap)))}
            data-held-copy={looksRight === false ? "lg.fix" : "lg.continue"}
          >
            {say4(looksRight === false ? "lg.fix" : "lg.continue")}
          </button>
        </>
      }
    >
      <div>
        <Said4 id="lg.heading" as="h3" className="lg-heading" />
        <p className="muted">{lgExplanation(gap)}</p>
      </div>
      {size !== null && (
        <section className="sheet-card" data-size-check>
          <Said4 id="lg.size.title" as="h3" />
          <p className="muted small">{size.line}</p>
          {size.within && <p className="faint"><Icon name="checkCircle" size={14} /> <Said4 id="lg.size.within" /></p>}
        </section>
      )}
      <section className="sheet-card" data-record-check>
        <Said4 id="lg.record.title" as="h3" />
        <Said4 id="lg.record.sub" as="p" className="muted small" />
        <div className="choice-row" role="group">
          <button type="button" className={looksRight === true ? "choice on" : "choice"} aria-pressed={looksRight === true} onClick={() => setLooksRight(true)}>
            {say4("lg.record.yes")}
          </button>
          <button type="button" className={looksRight === false ? "choice on" : "choice"} aria-pressed={looksRight === false} onClick={() => setLooksRight(false)}>
            {say4("lg.record.no")}
          </button>
        </div>
      </section>
      {gap.sizeMismatch && (
        <p className="mismatch"><Icon name="warn" size={14} /> <Said4 id="lg.mismatch" /></p>
      )}
    </Sheet4>
  );
}
